/**
 * GAME 6 — ROTOR (lights-out sur arcs de Truchet).
 * Issu du prototype (index_v8.html:1261-1373), durci ensuite (phase difficulté) :
 * - mélange chevauchant (Tchebychev ≤ 2) dès le niveau 1 pour les défis à forme ;
 * - Papillon : les deux damiers de parité reçoivent chacun leur part du mélange ;
 * - Onde/Tempête : cellules de mélange sur lignes ET colonnes toutes distinctes
 *   (casse les annulations, la profondeur nominale devient réelle) ;
 * - fenêtre de désordre initial par niveau (re-mélange ≤ 20, meilleur candidat) ;
 * - budget Économie resserré, plafond Épopée, `par` posé par gen.
 */
import type { GameEngine, PuzzleBase, Rng } from '@ordre-cache/shared';
import { ri, shuffle } from '@ordre-cache/shared';
import { deep, mk } from './util';

export interface RotorPuzzle extends PuzzleBase {
  g: 'rotor';
  target: number[][];
  cur: number[][];
  /**
   * Cellules du mélange (indices r*N+c). Les taps étant involutifs et
   * commutatifs, rejouer exactement ces cellules ramène cur sur target :
   * c'est la « solution par taps » du puzzle (utilisée par solvable-taps.test).
   */
  mix: number[];
  taps: number;
  budget?: number;
}

function roShape(di: number, r: number, c: number): number[][] | null {
  const cross = [
    [0, 0],
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
  ];
  const diag = [
    [0, 0],
    [-1, -1],
    [-1, 1],
    [1, -1],
    [1, 1],
  ];
  if (di === 0 || di === 3) return cross;
  if (di === 1) return diag;
  if (di === 5) return (r + c) % 2 === 0 ? cross : diag;
  return null; // di===2||di===4 rowcol
}

function roApply(di: number, N: number, cur: number[][], r: number, c: number): void {
  if (di === 2 || di === 4) {
    for (let i = 0; i < N; i++) {
      cur[r][i] ^= 1;
      cur[i][c] ^= 1;
    }
    cur[r][c] ^= 1;
  } else {
    for (const [dr, dc] of roShape(di, r, c)!) {
      const rr = r + dr,
        cc = c + dc;
      if (rr >= 0 && rr < N && cc >= 0 && cc < N) cur[rr][cc] ^= 1;
    }
  }
}

/**
 * Cellules de mélange des défis à forme (0/1/3/5).
 * Dès le niveau 1, chaque cellule après la première reste à distance de
 * Tchebychev ≤ 2 d'une cellule déjà tirée : les zones d'effet se chevauchent,
 * le glouton « je tape où l'orange baisse » ne suffit plus.
 * Papillon (di1) : chaque damier de parité reçoit ≥ ceil(K/3) cellules.
 * Passe 2 = repli : accepter sans contrainte (gen réussit toujours).
 */
function roCells(di: number, N: number, diff: number, K: number, rng: Rng): [number, number][] {
  const cells: [number, number][] = [];
  const used = new Set<string>();
  const minPar = di === 1 ? Math.ceil(K / 3) : 0;
  const par = [0, 0];
  for (let pass = 0; pass < 2 && cells.length < K; pass++) {
    for (let i = 0; i < K * 3 && cells.length < K; i++) {
      const r = ri(rng, N),
        c = ri(rng, N),
        key = r + '_' + c;
      if (used.has(key)) continue;
      if (pass === 0) {
        if (minPar > 0 && par[(r + c) % 2] >= K - minPar) continue;
        if (
          diff >= 1 &&
          cells.length > 0 &&
          !cells.some(([rr, cc]) => Math.max(Math.abs(rr - r), Math.abs(cc - c)) <= 2)
        )
          continue;
      }
      used.add(key);
      cells.push([r, c]);
      par[(r + c) % 2]++;
    }
  }
  return cells;
}

/** Onde/Tempête : K cellules sur lignes ET colonnes toutes distinctes (K ≤ N). */
function roCellsRowcol(N: number, K: number, rng: Rng): [number, number][] {
  const rows = shuffle(
    rng,
    Array.from({ length: N }, (_, i) => i),
  );
  const cols = shuffle(
    rng,
    Array.from({ length: N }, (_, i) => i),
  );
  const cells: [number, number][] = [];
  for (let i = 0; i < K; i++) cells.push([rows[i], cols[i]]);
  return cells;
}

export const ROTOR: GameEngine<RotorPuzzle> = {
  id: 'rotor',
  name: 'Rotor',
  icon: '🌀',
  accent: '#b388ff',
  tag: 'Chaque geste retourne aussi les voisins.',
  how: "Touche une case : <b>elle se retourne AVEC ses voisines</b> (la forme dépend du défi). But : reproduire exactement le <b>motif cible</b> (vignette en haut à droite). Les cases fausses sont oranges. C'est un puzzle de parité — l'ordre des coups ne compte pas !",
  sizes: [5, 8, 12, 18],
  defis: [
    { n: 'Écho', d: 'Chaque toucher retourne la tuile et ses quatre voisines.', ic: '➕' },
    {
      n: 'Papillon',
      d: 'Les ailes battent en diagonale : deux damiers indépendants à démêler — les ailes ne franchissent jamais le damier.',
      ic: '🦋',
    },
    { n: 'Onde', d: 'Un toucher, et toute la ligne et la colonne basculent.', ic: '🌊' },
    { n: 'Économie', d: 'Reproduis le motif en un nombre de coups limité.', ic: '🎯' },
    {
      n: 'Tempête',
      d: "L'onde, brassée sur des lignes et colonnes toutes différentes — aucune annulation gratuite.",
      ic: '🌪️',
    },
    { n: 'Vertige', d: 'Chaque case retourne à sa manière. Accroche-toi.', ic: '🎢' },
  ],
  gen(di, N, diff, rng) {
    const target = mk(N, 0).map((row) => row.map(() => ri(rng, 2)));
    const rowcol = di === 2 || di === 4;
    let K: number;
    if (di === 2) K = [3, Math.round(N * 0.6), Math.round(N * 0.8)][diff];
    else if (di === 4) K = [Math.round(N * 0.6), Math.round(N * 0.8), N][diff];
    else {
      K = [Math.max(3, Math.round(N * 0.6)), N, Math.round(N * 1.6)][diff];
      if (N === 12) K = Math.min(K, 14); // plafond Épopée : par jouable en famille
    }
    if (rowcol) K = Math.max(1, Math.min(K, N));
    // Fenêtre de cases fausses initiales visée (% de N²) — les contraintes de
    // mélange priment ; on garde le candidat le plus proche de la fenêtre.
    const [lo, hi] = [
      [15, 30],
      [30, 45],
      [45, 60],
    ][diff];
    const loM = (lo / 100) * N * N,
      hiM = (hi / 100) * N * N;
    let bestCur: number[][] = target,
      bestMix: number[] = [],
      bestDist = Infinity;
    for (let att = 0; att < 20 && bestDist > 0; att++) {
      const cur = deep(target);
      const cells = rowcol ? roCellsRowcol(N, K, rng) : roCells(di, N, diff, K, rng);
      for (const [r, c] of cells) roApply(di, N, cur, r, c);
      let m0 = 0;
      for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (cur[r][c] !== target[r][c]) m0++;
      if (m0 === 0) {
        // filet : jamais livrer un candidat déjà résolu
        const fr = ri(rng, N),
          fc = ri(rng, N);
        roApply(di, N, cur, fr, fc);
        cells.push([fr, fc]);
        for (let r = 0; r < N; r++)
          for (let c = 0; c < N; c++) if (cur[r][c] !== target[r][c]) m0++;
      }
      const dist = m0 < loM ? loM - m0 : m0 > hiM ? m0 - hiM : 0;
      if (dist < bestDist) {
        bestDist = dist;
        bestCur = cur;
        bestMix = cells.map(([r, c]) => r * N + c);
      }
    }
    const p: RotorPuzzle = {
      g: 'rotor',
      pid: ri(rng, 1e9),
      N,
      di,
      target,
      cur: bestCur,
      mix: bestMix,
      taps: 0,
      par: bestMix.length,
    };
    if (di === 3) p.budget = bestMix.length + [4, 2, 1][diff];
    return p;
  },
  tap(p, r, c) {
    roApply(p.di, p.N, p.cur, r, c);
    p.taps++;
    return true;
  },
  status(p) {
    const N = p.N;
    let m = 0;
    const rowBad = new Array<boolean>(N).fill(false),
      colBad = new Array<boolean>(N).fill(false);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++)
        if (p.cur[r][c] !== p.target[r][c]) {
          m++;
          rowBad[r] = true;
          colBad[c] = true;
        }
    let ok = m === 0,
      txt;
    if (p.budget != null) {
      ok = ok && p.taps <= p.budget;
      txt =
        m === 0
          ? p.taps <= p.budget
            ? ''
            : `😅 réussi mais en ${p.taps} coups (max ${p.budget}) — 🔀`
          : `🌀 <b>${m}</b> cases à accorder · coups <b>${p.taps}/${p.budget}</b>`;
      if (p.taps > p.budget && m > 0)
        txt = `💸 Budget dépassé (${p.taps}/${p.budget}) — 🔀 recommence`;
    } else txt = `🌀 <b>${m}</b> cases à accorder · ${p.taps} coups`;
    if ((p.di === 2 || p.di === 4) && m > 0) {
      const nr = rowBad.filter(Boolean).length,
        nc = colBad.filter(Boolean).length;
      txt += ` · 📐 <b>${nr}</b> lignes · <b>${nc}</b> colonnes à accorder`;
    }
    if (ok) txt = '🎉 <b>Le motif est accordé !</b>';
    return { ok, txt };
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N;
    const band = Math.max(50, S * 0.24),
      gs = S - band,
      cs = gs / N,
      ox = (S - gs) / 2,
      oy = band; // vignette dans une bande dédiée, jamais sur la grille
    ctx.fillStyle = '#efe6d0';
    ctx.fillRect(0, 0, S, S);
    const st = this.status(p);
    ctx.fillStyle = 'rgba(93,74,156,.1)';
    ctx.fillRect(0, 0, S, band);
    ctx.fillStyle = '#5d4a9c';
    ctx.font = `bold ${Math.min(15, band * 0.24)}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('🎯 Motif cible →', 10, band / 2);
    const ms = band - 12,
      mx = S - ms - 8,
      my = 6,
      mcs = ms / N;
    ctx.fillStyle = '#f6efdd';
    ctx.fillRect(mx - 3, my - 3, ms + 6, ms + 6);
    ctx.strokeStyle = '#5d4a9c';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(mx - 3, my - 3, ms + 6, ms + 6);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const x0 = mx + c * mcs,
          y0 = my + r * mcs;
        ctx.strokeStyle = '#5d4a9c';
        ctx.lineWidth = Math.max(1, mcs * 0.18);
        if (p.target[r][c] === 0) {
          ctx.beginPath();
          ctx.arc(x0, y0, mcs / 2, 0, Math.PI / 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x0 + mcs, y0 + mcs, mcs / 2, Math.PI, Math.PI * 1.5);
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.arc(x0 + mcs, y0, mcs / 2, Math.PI / 2, Math.PI);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x0, y0 + mcs, mcs / 2, Math.PI * 1.5, Math.PI * 2);
          ctx.stroke();
        }
      }
    ctx.lineCap = 'round';
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const x0 = ox + c * cs,
          y0 = oy + r * cs,
          v = p.cur[r][c],
          match = v === p.target[r][c];
        if (!match) {
          ctx.fillStyle = 'rgba(255,158,109,.28)';
          ctx.fillRect(x0, y0, cs, cs);
        }
        ctx.strokeStyle = st.ok ? '#7c4dff' : match ? '#5d4a9c' : '#e07840';
        ctx.lineWidth = cs * 0.16;
        if (st.ok) {
          ctx.shadowColor = '#b388ff';
          ctx.shadowBlur = cs * 0.25;
        }
        if (v === 0) {
          ctx.beginPath();
          ctx.arc(x0, y0, cs / 2, 0, Math.PI / 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x0 + cs, y0 + cs, cs / 2, Math.PI, Math.PI * 1.5);
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.arc(x0 + cs, y0, cs / 2, Math.PI / 2, Math.PI);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x0, y0 + cs, cs / 2, Math.PI * 1.5, Math.PI * 2);
          ctx.stroke();
        }
        ctx.shadowBlur = 0;
      }
    ctx.strokeStyle = 'rgba(93,74,156,.15)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= N; i++) {
      ctx.beginPath();
      ctx.moveTo(ox + i * cs, oy);
      ctx.lineTo(ox + i * cs, oy + gs);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(ox, oy + i * cs);
      ctx.lineTo(ox + gs, oy + i * cs);
      ctx.stroke();
    }
  },
  solveIt(p) {
    p.cur = deep(p.target);
  },
};
